import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PauseCircle } from "lucide-react";

interface Props {
  // Audit I3: backend now surfaces `last_pause_reason` on monitor responses.
  // We branch on its value to render the right copy.
  monitor: { id: string; status: string; last_pause_reason?: string | null };
  /**
   * Optional fallback line when last_pause_reason is null (manual pause).
   * Default is the generic line below.
   */
  reason?: string;
  onResume: () => void;
}

const REASON_COPY: Record<string, { title: string; description: string }> = {
  volume_cap: {
    title: "Monitor auto-paused — too many matches",
    description:
      "Confirmed match count exceeded the daily cap in the last 24 hours. Raise the cap or tighten the IR before resuming.",
  },
  consecutive_wrong: {
    title: "Monitor auto-paused — recent matches were marked wrong",
    description:
      "Three consecutive confirmed matches were marked wrong. Refine the IR (tighten polarity or add must-exclude terms) before resuming.",
  },
};

export function AutoPausedBanner({ monitor, reason, onResume }: Props) {
  if (monitor.status !== "paused") return null;
  const auto = monitor.last_pause_reason
    ? REASON_COPY[monitor.last_pause_reason]
    : null;
  const title = auto?.title ?? "Monitor paused";
  const description = auto?.description ?? reason ?? "This monitor is paused.";
  return (
    <Alert className="border-yellow-200 bg-yellow-50">
      <PauseCircle className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="mt-1 flex items-center justify-between">
        <span>{description}</span>
        <Button size="sm" variant="outline" onClick={onResume}>
          Resume
        </Button>
      </AlertDescription>
    </Alert>
  );
}
